/**
 * Reusable filter dropdown used across list pages (browse, lexicon,
 * constructions, history-by-skill, etc.).
 *
 * Two layouts:
 *   - "stacked" (default): label above, full-width select. Used in
 *     browse's filter row where each filter sits in a grid cell.
 *   - "inline": small label + small select on one row. Used in
 *     lexicon's two-filter chip row.
 *
 * Options can be passed as `string[]` (value === label) or as
 * `{ value, label }[]` when the two differ (e.g. skill code → name).
 * The "— any —" placeholder is rendered when value === "".
 */

export type FilterOption = { value: string; label: string };

type FilterSelectProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly FilterOption[] | readonly string[];
  /** Override the empty/clear option label. Default: "— any —". */
  placeholder?: string;
  /**
   * Hide the empty/clear option entirely. Default: false (clear is shown).
   * Use when the field is required (e.g. language picker).
   */
  required?: boolean;
  layout?: "stacked" | "inline";
};

export function FilterSelect({
  label,
  value,
  onChange,
  options,
  placeholder = "— any —",
  required = false,
  layout = "stacked",
}: FilterSelectProps) {
  const normalized: readonly FilterOption[] =
    typeof options[0] === "string" || options.length === 0
      ? (options as readonly string[]).map((o) => ({ value: o, label: o }))
      : (options as readonly FilterOption[]);

  if (layout === "inline") {
    return (
      <label className="text-sm flex items-center gap-2">
        <span className="eyebrow">{label}</span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="border border-ink-200 px-2 py-1 text-sm bg-paper hover:border-ink-400 focus:border-ink-700 transition-colors"
        >
          {!required && <option value="">{placeholder}</option>}
          {normalized.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <label className="block">
      <div className="eyebrow mb-1">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-ink-200 px-3 py-2 text-ink-900 hover:border-ink-400 focus:border-ink-900 focus:outline-none bg-paper transition-colors"
      >
        {!required && <option value="">{placeholder}</option>}
        {normalized.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
